/**
 * `@npc/api-client` —— 控制面 API 的 TypeScript 客户端。
 *
 * `schema.d.ts` 由 `openapi/openapi.yaml` 生成，**任何人不许手改**，
 * CI 会重新生成一遍再 diff。改接口形状 = 改契约 = 提一条流 0 任务。
 *
 * 这个文件里只有把 `openapi-fetch` 装配起来的那点胶水，不含任何接口定义。
 */
import createClient, { type Client, type ClientOptions } from 'openapi-fetch'

import type { components, operations, paths } from './schema'

export type { components, operations, paths }

/** 常用类型的短名字，省得到处写 `components['schemas']['Host']`。 */
export type Schemas = components['schemas']

export type ApiError = Schemas['Error']
export type Host = Schemas['Host']
export type HostSummary = Schemas['HostSummary']
export type HostStatus = Schemas['HostStatus']
export type HostLabel = Schemas['HostLabel']
export type HostLabelId = Schemas['HostLabelId']
export type HostSuggestion = Schemas['HostSuggestion']
export type Project = Schemas['Project']
export type Dashboard = Schemas['Dashboard']
export type Alert = Schemas['Alert']
export type NpcEvent = Schemas['Event']
export type OnboardingEvent = Schemas['OnboardingEvent']
export type SshParseResult = Schemas['SshParseResult']
export type TemporaryAccess = Schemas['TemporaryAccess']
export type MetricSeries = Schemas['MetricSeries']
export type SecurityCheck = Schemas['SecurityCheck']
export type StepUpIntent = Schemas['StepUpIntent']

export type NpcClient = Client<paths>

/** 危险动作用的请求头。值来自 `/auth/step-up/verify`。 */
export const STEP_UP_HEADER = 'X-Npc-Step-Up'

export interface CreateNpcClientOptions extends ClientOptions {
  /** 默认同源 `/api/v1`。壳里跑的时候（Capacitor / Tauri）传绝对地址。 */
  baseUrl?: string
}

/**
 * 建一个客户端。
 *
 * 登录态走 httpOnly Cookie，所以默认 `credentials: 'include'`。
 */
export function createNpcClient(options: CreateNpcClientOptions = {}): NpcClient {
  const { baseUrl = '/api/v1', ...rest } = options
  return createClient<paths>({
    baseUrl,
    credentials: 'include',
    ...rest,
  })
}

/** 把二次验证令牌拼成请求头。红级动作要带上它。 */
export function stepUpHeader(token: string): Record<string, string> {
  return { [STEP_UP_HEADER]: token }
}

/**
 * 判断一个未知的错误响应是不是我们的 `Error` 形状。
 *
 * 界面上只许显示 `user_message`。`code` 与 `developer_message` 是给开发看的，
 * 显示给用户就违反了「术语藏在第二层」。
 */
export function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApiError).code === 'string' &&
    typeof (value as ApiError).user_message === 'string'
  )
}
